import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Star, MessageSquare, UserPlus, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Page, Avatar, Empty } from "@/components/layout/AppShell";
import { PostCard, ServiceCard, ProductCard, MemberBadge, TypeBadge } from "@/components/Cards";
import { absUrl } from "@/components/Uploader";
import { ReportButton } from "@/components/ReportButton";

export default function ReaderProfile() {
  const { username } = useParams();
  const { user, features } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState<any>(null);
  const [posts, setPosts] = useState<any[]>([]);
  const [tab, setTab] = useState(new URLSearchParams(window.location.search).get("tab") || "posts");
  const [err, setErr] = useState("");

  useEffect(() => {
    const u = (username || "").replace("@", "");
    api.get(`/readers/by-username/${u}`).then((r) => { setData(r.data); return api.get(`/posts/by-author/${r.data.user.id}`); })
      .then((r) => setPosts(r.data)).catch((e) => setErr(errMsg(e)));
  }, [username]);

  if (err) return <Page><Empty title="Reader not found." /></Page>;
  if (!data) return <Page><div className="card-tactile h-64 animate-pulse" /></Page>;
  const r = data.user;
  const mine = user?.id === r.id;

  const follow = async () => {
    if (!user) return nav("/login");
    try { const { data: d } = await api.post(`/readers/${r.id}/follow`); setData({ ...data, following: d.following, user: { ...r, followers_count: r.followers_count + (d.following ? 1 : -1) } }); } catch (e) { toast.error(errMsg(e)); }
  };
  const message = async () => {
    if (!user) return nav("/login");
    const body = window.prompt(`Message ${r.name}`);
    if (!body) return;
    try { const { data: c } = await api.post("/conversations", { user_id: r.id, body }); nav(`/inbox/${c.id}`); } catch (e) { toast.error(errMsg(e)); }
  };

  const tabs = [["posts", "Posts"], ["services", "Readings"], ...(features.shop ? [["shop", "Shop"]] : []), ...(features.circles ? [["circle", "Circle"]] : []), ["about", "About & Reviews"]];
  const joinCircle = async () => {
    if (!user) return nav(`/login?next=/@${r.username}`);
    try { const { data: c } = await api.post("/payments/checkout", { kind: "circle", ref_id: data.circle.id, origin_url: window.location.origin }); window.location.href = c.checkout_url; } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="reader-profile">
      <div className="h-44 sm:h-64 bg-[#1A1917] overflow-hidden relative">
        {r.cover && <img src={absUrl(r.cover)} alt="" className="h-full w-full object-cover opacity-70" />}
      </div>
      <Page className="!pt-0">
        <div className="-mt-12 sm:-mt-16 flex flex-col sm:flex-row sm:items-end gap-4 sm:gap-6">
          <div className="ring-4 ring-[#F6F4EE] rounded-full w-fit"><Avatar u={r} size={112} /></div>
          <div className="flex-1 sm:pb-2">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-4xl sm:text-5xl font-semibold leading-none" data-testid="profile-name">{r.name}</h1>
              <TypeBadge type={r.creator_type} className="!text-[10px] !px-1.5 !py-0.5" />
              {r.verified && <span className="mono text-[9px] text-[#C5A059] border border-[#C5A059] px-1.5 py-0.5">VERIFIED</span>}
            </div>
            <p className="mono text-xs text-[#5E5C56] mt-2">@{r.username} · {r.followers_count} followers · <Star size={11} className="inline -mt-0.5" /> {r.rating || "—"} ({r.rating_count} reviews)</p>
          </div>
          <div className="flex gap-2 sm:pb-2">
            {!mine && <button onClick={follow} className={data.following ? "btn-ghost" : "btn-wine"} data-testid="follow-btn">{data.following ? <><UserCheck size={16} /> Following</> : <><UserPlus size={16} /> Follow</>}</button>}
            {!mine && <button onClick={message} className="btn-ghost" data-testid="message-btn"><MessageSquare size={16} /> Message</button>}
            {!mine && <ReportButton type="reader" id={r.id} className="self-center px-2" />}
            {mine && <Link to="/dashboard" className="btn-ghost">Edit in dashboard</Link>}
          </div>
        </div>
        <p className="mt-6 max-w-2xl serif text-xl italic text-[#2C2A28] leading-snug" data-testid="profile-bio">“{r.bio}”</p>
        <div className="flex flex-wrap gap-1.5 mt-4">{(r.specialties || []).map((s: string) => <Link key={s} to={`/search?specialty=${s}`} className="chip">{s}</Link>)}</div>

        <div className="flex gap-1 mt-8 border-b border-[#D8D4C9] overflow-x-auto scrollbar-none">
          {tabs.map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={`px-4 py-2.5 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${tab === k ? "border-[#581C25] text-[#1A1917]" : "border-transparent text-[#5E5C56]"}`} data-testid={`profile-tab-${k}`}>{l}</button>)}
        </div>
        <div className="mt-6">
          {tab === "posts" && (posts.length ? <div className="grid lg:grid-cols-2 gap-4">{posts.map((p) => <PostCard key={p.id} post={p} />)}</div> : <Empty title="No posts yet." />)}
          {tab === "services" && (data.services.length ? <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{data.services.map((s: any) => <ServiceCard key={s.id} s={{ ...s, reader: r }} showReader={false} />)}</div> : <Empty title="No reading services listed." />)}
          {tab === "shop" && (data.products.length ? <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{data.products.map((p: any) => <ProductCard key={p.id} p={{ ...p, reader: r }} />)}</div> : <Empty title="Shop is empty." />)}
          {tab === "circle" && (
            data.circle ? (
              <div className="grid md:grid-cols-[1.2fr_1fr] gap-6" data-testid="circle-panel">
                <div className="rounded-md border border-[#C5A059] bg-[#121110] text-[#F6F4EE] p-6 sm:p-8">
                  <p className="eyebrow !text-[#C5A059] mb-2">Circle membership</p>
                  <h2 className="text-3xl font-semibold leading-tight" data-testid="circle-name">{data.circle.name}</h2>
                  <p className="text-[#A8A59C] mt-3 leading-relaxed">{data.circle.description}</p>
                  <ul className="mt-5 space-y-2">{(data.circle.perks || []).map((pk: string) => <li key={pk} className="flex items-center gap-2 text-sm"><Star size={12} className="text-[#C5A059]" />{pk}</li>)}</ul>
                  <p className="mono text-xs text-[#A8A59C] mt-6">{data.circle.members_count} member{data.circle.members_count === 1 ? "" : "s"}</p>
                </div>
                <div className="card-tactile p-6 h-fit">
                  <p className="mono text-3xl font-semibold" data-testid="circle-price">${Number(data.circle.price_monthly).toFixed(2)}<span className="text-sm text-[#5E5C56] font-normal"> / month</span></p>
                  {mine ? <Link to="/dashboard?tab=circles" className="btn-ghost w-full mt-4">Manage in dashboard</Link> :
                    data.membership ? <div className="mt-4 space-y-2"><p className="text-sm flex items-center gap-2"><MemberBadge /> You're a member</p><Link to="/you?tab=circles" className="btn-ghost w-full">Manage membership</Link></div> :
                    <button onClick={joinCircle} className="btn-wine w-full mt-4" data-testid="join-circle-btn">Join the Circle</button>}
                  <p className="text-[11px] text-[#5E5C56] mt-3">Billed monthly via Stripe. Cancel any time.</p>
                </div>
              </div>
            ) : <Empty title="No Circle yet." hint={mine ? "Create one from your dashboard." : "This reader hasn't opened a membership."} />)}
          {tab === "about" && (
            <div className="grid md:grid-cols-[1fr_1.4fr] gap-8">
              <div className="card-tactile p-6"><p className="eyebrow mb-3">About</p><p className="text-sm leading-relaxed">{r.bio}</p><p className="mono text-xs text-[#5E5C56] mt-4">{r.years_experience || 0} years of practice</p></div>
              <div className="space-y-3">
                <p className="eyebrow">Reviews</p>
                {data.reviews.length === 0 && <p className="text-sm text-[#5E5C56]">No reviews yet.</p>}
                {data.reviews.map((rv: any) => (
                  <div key={rv.id} className="card-tactile p-4" data-testid={`review-${rv.id}`}>
                    <div className="flex items-center justify-between"><span className="text-sm font-medium flex items-center gap-2">{rv.author?.name}{rv.circle_member && <MemberBadge />}</span><span className="mono text-xs text-[#C5A059]">{"★".repeat(rv.rating)}</span></div>
                    <p className="text-sm text-[#2C2A28] mt-1">{rv.body}</p><p className="mono text-[10px] text-[#5E5C56] mt-2">{rv.service_title}</p>
                  </div>))}
              </div>
            </div>)}
        </div>
      </Page>
    </div>
  );
}

export function PostPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [post, setPost] = useState<any>(null);
  const [body, setBody] = useState("");
  const load = () => api.get(`/posts/${id}`).then((r) => setPost(r.data));
  useEffect(() => { load(); }, [id]);
  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return nav("/login");
    try { await api.post(`/posts/${id}/comments`, { body }); setBody(""); load(); } catch (er) { toast.error(errMsg(er)); }
  };
  if (!post) return <Page><div className="card-tactile h-64 animate-pulse" /></Page>;
  return (
    <Page className="max-w-2xl">
      <PostCard post={post} />
      <section className="mt-6">
        <p className="eyebrow mb-3">Comments · {post.comments.length}</p>
        <form onSubmit={send} className="flex gap-2 mb-4">
          <input className="input-ed" placeholder="Add a comment" value={body} onChange={(e) => setBody(e.target.value)} data-testid="comment-input" />
          <button className="btn-wine" disabled={!body.trim()} data-testid="comment-submit">Post</button>
        </form>
        <div className="space-y-3">{post.comments.map((c: any) => (
          <div key={c.id} className="flex gap-3" data-testid={`comment-${c.id}`}><Avatar u={c.author} size={32} /><div><p className="text-sm"><span className="font-medium">{c.author?.name}</span> {c.circle_member && <MemberBadge />} <span className="text-[#2C2A28]">{c.body}</span></p></div></div>))}</div>
      </section>
    </Page>
  );
}
